import { useTranslation } from "react-i18next";
import { Upload, UserPlus, ScrollText, FilePlus2, ArrowLeft, ChevronRight } from "lucide-react";
import { Avatar } from "@/shared/ui/Avatar";
import { ImportHistoryPanel } from "../ImportHistoryPanel";
import type { ImportMode, Merchant } from "../types";

// Customer-related import methods: bulk file upload or single manual entry.
const CUSTOMER_MODES: {
  mode: ImportMode;
  icon: typeof Upload;
  titleKey: string;
  hintKey: string;
}[] = [
  {
    mode: "file",
    icon: Upload,
    titleKey: "import.modeStep.fileMode",
    hintKey: "import.modeStep.fileModeHint",
  },
  {
    mode: "manual",
    icon: UserPlus,
    titleKey: "import.modeStep.manualMode",
    hintKey: "import.modeStep.manualModeHint",
  },
];

// Agreement-related import methods: bulk XLSX import or single manual entry via side drawer.
const AGREEMENT_MODES: {
  mode: ImportMode;
  icon: typeof Upload;
  titleKey: string;
  hintKey: string;
}[] = [
  {
    mode: "agreements",
    icon: ScrollText,
    titleKey: "import.modeStep.agreementMode",
    hintKey: "import.modeStep.agreementModeHint",
  },
  {
    // "agreement-manual" opens the AgreementAddDrawer (wired in ImportScreen by subtask 0119.03)
    mode: "agreement-manual",
    icon: FilePlus2,
    titleKey: "import.modeStep.agreementManualMode",
    hintKey: "import.modeStep.agreementManualModeHint",
  },
];

// Renders a single method card with icon, title, hint, and chevron arrow.
function ModeCard({
  mode,
  icon: Icon,
  titleKey,
  hintKey,
  onSelect,
}: {
  mode: ImportMode;
  icon: typeof Upload;
  titleKey: string;
  hintKey: string;
  onSelect: (mode: ImportMode) => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      key={mode}
      type="button"
      onClick={() => onSelect(mode)}
      className="group flex items-center gap-4 rounded-card border-2 border-grey-200 bg-white p-4 text-left transition-all hover:border-green hover:shadow-card active:scale-[0.99]"
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-control bg-green-bg text-green">
        <Icon size={24} strokeWidth={1.8} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-semibold text-grey-800">{t(titleKey)}</div>
        <div className="mt-0.5 text-[13px] text-grey-500">{t(hintKey)}</div>
      </div>
      <ChevronRight
        size={18}
        className="shrink-0 text-grey-300 transition-colors group-hover:text-green"
      />
    </button>
  );
}

// Step 2 — pick the import method.
// Cards are grouped into two semantic sections: "Mijozlar" (customers) and "Shartnomalar" (agreements).
// Shows the selected merchant as a chip on top.
export function ModeStep({
  merchant,
  onBack,
  onSelect,
}: {
  merchant: Merchant;
  onBack: () => void;
  onSelect: (mode: ImportMode) => void;
}) {
  const { t } = useTranslation();
  const name = `${merchant.first_name} ${merchant.last_name}`.trim();

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex items-center gap-1.5 text-[13px] text-grey-500 hover:text-grey-700"
      >
        <ArrowLeft size={14} strokeWidth={2} />
        {t("import.back")}
      </button>

      {/* Selected-merchant chip */}
      <div className="mb-5 flex items-center gap-3 rounded-card border border-grey-200 bg-white px-4 py-3">
        <Avatar name={name} size="md" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-semibold text-grey-800">{name}</div>
          <div className="text-[12px] text-grey-400">{merchant.phone_number}</div>
        </div>
        <span className="shrink-0 rounded-pill bg-grey-100 px-2.5 py-0.5 text-[12px] text-grey-600 tnum">
          {t("import.merchantStep.clientCount", { count: merchant.client_count })}
        </span>
      </div>

      <h2 className="mb-4 text-[18px] font-semibold text-grey-800">{t("import.modeStep.title")}</h2>

      {/* Two semantic sections with their own 2-column grids */}
      <div className="space-y-6">
        {/* Section 1: Mijozlar (customers) — file import + manual single entry */}
        <div>
          <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-wide text-grey-400">
            {t("import.modeStep.sectionCustomers")}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {CUSTOMER_MODES.map((item) => (
              <ModeCard key={item.mode} {...item} onSelect={onSelect} />
            ))}
          </div>
        </div>

        {/* Section 2: Shartnomalar (agreements) — XLSX import + manual single agreement */}
        <div>
          <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-wide text-grey-400">
            {t("import.modeStep.sectionAgreements")}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {AGREEMENT_MODES.map((item) => (
              <ModeCard key={item.mode} {...item} onSelect={onSelect} />
            ))}
          </div>
        </div>
      </div>

      {/* Recent imports for this merchant, shown below the mode picker */}
      <div className="mt-8">
        <ImportHistoryPanel merchantId={merchant.id} />
      </div>
    </div>
  );
}
